import React, { useState } from 'react';
import { ChevronRight, Info, MessageCircle, HandHeart, Users as UsersIcon, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const EngagementLadder = () => {
  const [activeLevel, setActiveLevel] = useState(null);

  const levels = [
    {
      id: 'inform',
      title: 'Inform',
      subtitle: 'Citizens as Recipients',
      icon: Info,
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      iconBg: 'bg-blue-100',
      description: 'One-way communication from government to citizens. This level involves providing citizens with information about legislative activities, decisions, and their rights.',
      examples: ['Public notices', 'Legislative broadcasts', 'Educational materials', 'Official announcements'],
      impact: 'Low - Limited to awareness',
      howToParticipate: 'Follow official National Assembly channels, attend informational sessions, read published materials'
    },
    {
      id: 'consult',
      title: 'Consult',
      subtitle: 'Citizens as Advisors',
      icon: MessageCircle,
      color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      iconBg: 'bg-emerald-100',
      description: 'Government requests and receives citizen feedback. This involves limited opportunities for citizens to influence outcomes.',
      examples: ['Public hearings', 'Surveys and questionnaires', 'Comment periods', 'Consultation sessions'],
      impact: 'Low to Moderate - Opinions considered but no guarantee of influence',
      howToParticipate: 'Submit feedback during comment periods, participate in surveys, attend public hearings'
    },
    {
      id: 'involve',
      title: 'Involve',
      subtitle: 'Citizens as Contributors',
      icon: HandHeart,
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      iconBg: 'bg-purple-100',
      description: 'Citizens work directly with legislators throughout the process, with multiple opportunities to influence legislation.',
      examples: ['Stakeholder meetings', 'Focus groups', 'Town halls', 'Working sessions'],
      impact: 'Moderate - Consistent dialogue ensures public concerns are considered',
      howToParticipate: 'Join stakeholder meetings, participate in focus groups, organize or attend town halls with representatives'
    },
    {
      id: 'collaborate',
      title: 'Collaborate',
      subtitle: 'Citizens as Partners',
      icon: UsersIcon,
      color: 'bg-amber-50 border-amber-200 text-amber-700',
      iconBg: 'bg-amber-100',
      description: 'Joint decision-making and shared responsibility, where citizens actively participate in developing alternatives and solutions.',
      examples: ['Working groups', 'Citizen advisory committees', 'Collaborative drafting', 'Partnership programs'],
      impact: 'High - Partnership approach with significant public influence',
      howToParticipate: 'Join advisory committees, participate in working groups, engage in collaborative bill drafting sessions'
    },
    {
      id: 'empower',
      title: 'Empower',
      subtitle: 'Citizens as Decision-Makers',
      icon: Crown,
      color: 'bg-pink-50 border-pink-200 text-pink-700',
      iconBg: 'bg-pink-100',
      description: 'Final decision-making authority is placed in citizens\' hands. Representatives implement what citizens decide.',
      examples: ['Citizen assemblies with binding recommendations', 'Referendums', 'Participatory budgeting', 'Direct democracy initiatives'],
      impact: 'Highest - Citizens have direct control over outcomes',
      howToParticipate: 'Participate in referendums, join citizen assemblies, engage in participatory budgeting processes'
    }
  ];

  return (
    <div className="py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Engagement Ladder
          </h1>
          <p className="text-lg text-slate-600">
            Understand the five levels of citizen engagement in the legislative process. 
            Click each level to learn more about how you can participate.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8 shadow-sm">
          <p className="text-slate-600 leading-relaxed">
            The Engagement Ladder illustrates progressive levels of citizen involvement in legislative processes. 
            Moving up the ladder requires increasing commitments from both citizens and representatives, 
            offering greater public impact and democratic legitimacy.
          </p>
        </div>

        <div className="space-y-4">
          {levels.map((level, index) => {
            const IconComponent = level.icon;
            const isActive = activeLevel === level.id;

            return (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div
                  className={`border-2 rounded-xl overflow-hidden transition-all cursor-pointer ${
                    isActive ? 'shadow-lg' : 'shadow-sm hover:shadow-md'
                  } ${level.color}`}
                  onClick={() => setActiveLevel(isActive ? null : level.id)}
                  data-testid={`engagement-level-${level.id}`}
                >
                  <div className="p-6 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-3xl font-heading font-bold text-slate-300">
                            {index + 1}
                          </span>
                          <div className={`w-12 h-12 rounded-lg ${level.iconBg} flex items-center justify-center`}>
                            <IconComponent className={`h-6 w-6 ${level.color.split(' ')[2]}`} />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-heading text-2xl font-semibold text-slate-900">
                            {level.title}
                          </h3>
                          <p className="text-sm text-slate-600">{level.subtitle}</p>
                        </div>
                      </div>
                      <ChevronRight
                        className={`h-6 w-6 text-slate-400 transition-transform ${
                          isActive ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </div>

                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t-2 border-slate-100"
                      >
                        <div className="p-6 bg-white space-y-6">
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2">Description</h4>
                            <p className="text-slate-600 leading-relaxed">{level.description}</p>
                          </div>

                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2">Examples</h4>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {level.examples.map((example, idx) => (
                                <li key={idx} className="flex items-center space-x-2 text-slate-600">
                                  <span className="w-1.5 h-1.5 bg-primary-700 rounded-full" />
                                  <span>{example}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2">Citizen Impact</h4>
                            <p className="text-slate-600">{level.impact}</p>
                          </div>

                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2">How to Participate</h4>
                            <p className="text-slate-600 leading-relaxed">{level.howToParticipate}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 bg-primary-50 rounded-2xl p-8 border border-primary-100">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
            Moving Up the Ladder
          </h3>
          <p className="text-slate-700 leading-relaxed">
            The most effective citizen engagement strategies employ multiple rungs of the ladder to create 
            diverse opportunities for participation. Within Nigeria's evolving democracy, engagement practices 
            continue to develop as the House of Representatives and citizens discover more effective ways to 
            work together in shaping the nation's laws and policies.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EngagementLadder;
